/**
 * Invite Template Modal Component
 * Handles WhatsApp invite template sending with parameter editing and preview
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import { getInviteTemplatePreview, getAvailableTemplates } from "../../utils/API_SERVICE_TWILIO";
import SearchableDropdown from "../../components/common/SearchableDropdown";

// Template name mapping: Technical name -> User-friendly display name
const TEMPLATE_NAME_MAP = {
  "cre_clinical_consultation_invite": "Clinical Consultation Invite",
  "radiologist_referring_physician_imaging_study_notification_en": "Radiologist Notification (English)",
  "radiologist_referring_physician_imaging_study_notification_he": "Radiologist Notification (Hebrew)",
};

function InviteTemplateModal({
  isOpen,
  onClose,
  onSend,
  language = "en",
  defaultParam1 = "",
  defaultParam2 = "",
  defaultParam3 = "",
  studyId = "",
  accessionNo = "",
} = {}) {
  const { t } = useTranslation("common");
  const isRTL = language === "he" || language === "ar";
  const [inviteParam1, setInviteParam1] = useState(defaultParam1);
  const [inviteParam2, setInviteParam2] = useState(defaultParam2);
  const [inviteParam3, setInviteParam3] = useState(defaultParam3);
  const [inviteBodyTemplate, setInviteBodyTemplate] = useState("");
  const [invitePreviewLoading, setInvitePreviewLoading] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [inviteErrors, setInviteErrors] = useState({ param1: "", param2: "", param3: "" });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateNeedsParam3, setTemplateNeedsParam3] = useState(true); // Default to true for backward compatibility
  const templateParam3CacheRef = useRef({}); // Cache template SID -> needsParam3 mapping
  const previewAbortControllerRef = useRef(null);
  const previewDebounceTimerRef = useRef(null);

  // Load available templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, language]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const result = await getAvailableTemplates({ language });
      const templates = (result.templates || []).map(t => {
        // Get user-friendly name from mapping, fallback to friendlyName, then sid
        const displayName = TEMPLATE_NAME_MAP[t.friendlyName] || t.friendlyName || t.sid;
        // Add language suffix only if not already in display name
        const languageSuffix = t.language && !displayName.toLowerCase().includes(t.language.toLowerCase()) 
          ? ` (${t.language.toUpperCase()})` 
          : "";
        
        return {
          value: t.sid,
          label: `${displayName}${languageSuffix}`,
          sid: t.sid,
          friendlyName: t.friendlyName,
          language: t.language,
        };
      });
      setAvailableTemplates(templates);
      
      // Auto-select first template if available and none selected
      if (templates.length > 0 && !selectedTemplate) {
        const firstTemplate = templates[0];
        setSelectedTemplate(firstTemplate.sid);
        
        // Set param2 based on template language: Hebrew uses accessionNo, others use studyId
        const isHebrewTemplate = firstTemplate.language?.toLowerCase() === "he";
        if (isHebrewTemplate && accessionNo) {
          setInviteParam2(accessionNo);
        } else if (!isHebrewTemplate && studyId) {
          setInviteParam2(studyId);
        }
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
      toast.error(t("chatTemplatesLoadFailed") || "Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Update local state when defaults change
  useEffect(() => {
    if (isOpen) {
      setInviteParam1(defaultParam1);
      // Don't reset param2 if a template is selected - it will be set based on template language
      // Otherwise use defaultParam2
      if (selectedTemplate) {
        const selectedTemplateData = availableTemplates.find(t => t.sid === selectedTemplate);
        const isHebrewTemplate = selectedTemplateData?.language?.toLowerCase() === "he";
        if (isHebrewTemplate && accessionNo) {
          setInviteParam2(accessionNo);
        } else if (!isHebrewTemplate && studyId) {
          setInviteParam2(studyId);
        } else {
          setInviteParam2(defaultParam2);
        }
      } else {
        setInviteParam2(defaultParam2);
      }
      setInviteParam3(defaultParam3);
      setInviteErrors({ param1: "", param2: "", param3: "" });
    }
  }, [isOpen, defaultParam1, defaultParam2, defaultParam3, selectedTemplate, availableTemplates, studyId, accessionNo]);

  const buildInvitePreview = useCallback(async (param1, param2, param3, contentSid) => {
    if (!isOpen) return;
    
    // Don't fetch preview if no template selected
    if (!contentSid) {
      setInviteBodyTemplate(t("chatSelectTemplateFirst") || "Please select a template first");
      return;
    }
    
    // Cancel previous request if in progress
    if (previewAbortControllerRef.current) {
      previewAbortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    previewAbortControllerRef.current = abortController;
    
    setInvitePreviewLoading(true);
    try {
      const result = await getInviteTemplatePreview({ 
        contentSid,
        language,
        signal: abortController.signal,
      });
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      console.log("Preview result:", result);
      const body = result?.body || "";
      if (body) {
        // Detect if template needs {{3}} parameter
        const needsParam3 = /\{\{3\}\}/.test(body);
        
        // Cache the result for this template SID
        templateParam3CacheRef.current[contentSid] = needsParam3;
        
        // Only update state if this is the currently selected template
        // (prevents race conditions if user changes template quickly)
        setTemplateNeedsParam3(needsParam3);
        
        let preview = body;
        // Replace {{1}}, {{2}}, {{3}} with actual values
        preview = preview.replace(/\{\{1\}\}/g, param1 || "{{1}}");
        preview = preview.replace(/\{\{2\}\}/g, param2 || "{{2}}");
        if (needsParam3) {
          preview = preview.replace(/\{\{3\}\}/g, param3 || "{{3}}");
        }
        setInviteBodyTemplate(preview);
      } else {
        console.warn("Preview body is empty:", result);
        setInviteBodyTemplate(t("chatInvitePreviewUnavailable") || "Preview unavailable");
        // Default to requiring param3 if we can't detect
        templateParam3CacheRef.current[contentSid] = true;
        setTemplateNeedsParam3(true);
      }
    } catch (err) {
      // Don't show error if request was aborted
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      console.error("Preview error:", err);
      console.error("Error details:", err.message, err.stack);
      setInviteBodyTemplate(`${t("chatInvitePreviewUnavailable") || "Preview unavailable"}: ${err.message || "Unknown error"}`);
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setInvitePreviewLoading(false);
      }
    }
  }, [isOpen, language, t]);

  // Initial load when modal opens (no debounce)
  useEffect(() => {
    if (isOpen && selectedTemplate) {
      // Load immediately with current values
      buildInvitePreview(inviteParam1, inviteParam2, inviteParam3, selectedTemplate);
    } else {
      // Cleanup on modal close
      if (previewAbortControllerRef.current) {
        previewAbortControllerRef.current.abort();
      }
      if (previewDebounceTimerRef.current) {
        clearTimeout(previewDebounceTimerRef.current);
      }
    }
    
    return () => {
      if (previewAbortControllerRef.current) {
        previewAbortControllerRef.current.abort();
      }
      if (previewDebounceTimerRef.current) {
        clearTimeout(previewDebounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedTemplate, buildInvitePreview]); // buildInvitePreview is stable callback

  // Debounced effect for parameter changes (only when typing)
  useEffect(() => {
    // Skip if modal is not open or no template selected
    if (!isOpen || !selectedTemplate) return;
    
    // Clear existing debounce timer
    if (previewDebounceTimerRef.current) {
      clearTimeout(previewDebounceTimerRef.current);
    }
    
    // Set new debounce timer (800ms delay after typing stops)
    previewDebounceTimerRef.current = setTimeout(() => {
      buildInvitePreview(inviteParam1, inviteParam2, inviteParam3, selectedTemplate);
    }, 800);
    
    return () => {
      if (previewDebounceTimerRef.current) {
        clearTimeout(previewDebounceTimerRef.current);
      }
    };
  }, [inviteParam1, inviteParam2, inviteParam3, selectedTemplate, isOpen, buildInvitePreview]);

  const validateInviteParams = () => {
    const errors = { param1: "", param2: "", param3: "" };
    let isValid = true;

    // Validate Parameter 1 (Required)
    if (!inviteParam1 || !inviteParam1.trim()) {
      errors.param1 = t("chatInviteParam1Required") || "Parameter 1 is required";
      isValid = false;
    }

    // Validate Parameter 2 (Required)
    if (!inviteParam2 || !inviteParam2.trim()) {
      errors.param2 = t("chatInviteParam2Required") || "Parameter 2 is required";
      isValid = false;
    }

    // Validate Parameter 3 (Only if template needs it)
    if (templateNeedsParam3) {
      if (!inviteParam3 || !inviteParam3.trim()) {
        errors.param3 = t("chatInviteParam3Required") || "Parameter 3 (Link) is required";
        isValid = false;
      } else {
        // Validate URL format for parameter 3
        try {
          new URL(inviteParam3.trim());
        } catch {
          errors.param3 = t("chatInviteParam3InvalidUrl") || "Please enter a valid URL";
          isValid = false;
        }
      }
    }

    setInviteErrors(errors);
    return isValid;
  };

  const handleSendInvite = async () => {
    if (!onSend) return;
    
    // Validate template selection
    if (!selectedTemplate) {
      toast.error(t("chatSelectTemplateRequired") || "Please select a template");
      return;
    }
    
    // Validate before sending
    if (!validateInviteParams()) {
      toast.error(t("chatInviteValidationFailed") || "Please fill all required fields correctly");
      return;
    }

    setIsSendingInvite(true);
    try {
      // Build contentVariables based on what template needs
      const contentVariables = {
        "1": inviteParam1.trim(),
        "2": inviteParam2.trim(),
      };
      
      // Only add {{3}} if template needs it
      if (templateNeedsParam3 && inviteParam3?.trim()) {
        contentVariables["3"] = inviteParam3.trim();
      }
      
      await onSend({
        contentSid: selectedTemplate,
        contentVariables,
        language,
      });
      toast.success(t("chatInviteSent") || "Invite sent successfully");
      onClose();
      // Reset form
      setInviteParam1("");
      setInviteParam2("");
      setInviteParam3("");
      setSelectedTemplate(null);
      setInviteErrors({ param1: "", param2: "", param3: "" });
    } catch (err) {
      console.error("Send invite error:", err);
      toast.error(t("chatSendFailed") || "Failed to send invite");
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleParamChange = (param, value) => {
    // Clear error for this parameter when user starts typing
    setInviteErrors((prev) => ({ ...prev, [param]: "" }));
    
    if (param === "param1") {
      setInviteParam1(value);
    } else if (param === "param2") {
      setInviteParam2(value);
    } else if (param === "param3") {
      setInviteParam3(value);
    }
  };

  const handleClose = () => {
    setInviteParam1("");
    setInviteParam2("");
    setInviteParam3("");
    setSelectedTemplate(null);
    setInviteErrors({ param1: "", param2: "", param3: "" });
    onClose();
  };

  const handleTemplateChange = (templateSid) => {
    // Only update if template actually changed
    if (templateSid === selectedTemplate) {
      return; // Don't do anything if same template selected
    }
    
    setSelectedTemplate(templateSid);
    
    // Find the selected template to check its language
    const selectedTemplateData = availableTemplates.find(t => t.sid === templateSid);
    const isHebrewTemplate = selectedTemplateData?.language?.toLowerCase() === "he";
    
    // Update param2 based on template language: Hebrew uses accessionNo, others use studyId
    if (isHebrewTemplate && accessionNo) {
      setInviteParam2(accessionNo);
    } else if (!isHebrewTemplate && studyId) {
      setInviteParam2(studyId);
    }
    
    // Check cache first - if we've seen this template before, use cached value
    if (templateParam3CacheRef.current.hasOwnProperty(templateSid)) {
      setTemplateNeedsParam3(templateParam3CacheRef.current[templateSid]);
    } else {
      // Default to true until we detect otherwise from preview
      setTemplateNeedsParam3(true);
    }
    
    // Clear param3 error when template changes
    setInviteErrors(prev => ({ ...prev, param3: "" }));
    // Preview will update automatically via useEffect
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50"
      onClick={handleClose}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 border dark:border-gray-700 max-h-[90vh] flex flex-col min-w-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="p-6 pb-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("chatInviteTitle") || "Send WhatsApp Invite"}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("chatInviteDescription") || "Edit the template parameters below:"}
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 pt-4 min-w-0">
          <div className="space-y-4 mb-4">
            {/* Template Selection Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("chatSelectTemplate") || "Select Template"} <span className="text-red-500">*</span>
              </label>
              {templatesLoading ? (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2">
                  <FaSpinner className="animate-spin" size={14} />
                  <span className="text-sm">{t("loading") || "Loading templates..."}</span>
                </div>
              ) : (
                <SearchableDropdown
                  label="Template"
                  options={availableTemplates}
                  value={selectedTemplate || ""}
                  onChange={handleTemplateChange}
                  placeholder={t("chatSelectTemplatePlaceholder") || "Choose a template..."}
                  emptyText={t("chatNoTemplatesAvailable") || "No templates available"}
                  emptySearchText={t("chatNoTemplatesMatch") || "No templates match your search"}
                  dir={isRTL ? "rtl" : "ltr"}
                />
              )}
              {!selectedTemplate && availableTemplates.length > 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {t("chatSelectTemplateRequired") || "Please select a template"}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("chatInviteParam1") || "Parameter 1 ({{1}})"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={inviteParam1}
                onChange={(e) => handleParamChange("param1", e.target.value)}
                placeholder={t("chatInviteParam1Placeholder") || "Recipient Name"}
                className={`w-full rounded-lg border ${
                  inviteErrors.param1 
                    ? "border-red-500 dark:border-red-500" 
                    : "dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]`}
                dir={isRTL ? "rtl" : "ltr"}
              />
              {inviteErrors.param1 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{inviteErrors.param1}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("chatInviteParam2") || "Parameter 2 ({{2}})"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={inviteParam2}
                onChange={(e) => handleParamChange("param2", e.target.value)}
                placeholder={t("chatInviteParam2Placeholder") || "Case ID"}
                className={`w-full rounded-lg border ${
                  inviteErrors.param2 
                    ? "border-red-500 dark:border-red-500" 
                    : "dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]`}
                dir={isRTL ? "rtl" : "ltr"}
              />
              {inviteErrors.param2 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{inviteErrors.param2}</p>
              )}
            </div>
            {/* Parameter 3 - Only show if template needs it */}
            {templateNeedsParam3 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("chatInviteParam3") || "Parameter 3 ({{3}})"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={inviteParam3}
                  onChange={(e) => handleParamChange("param3", e.target.value)}
                  placeholder={t("chatInviteParam3Placeholder") || "Secure Link (e.g., https://example.com)"}
                  className={`w-full min-w-0 rounded-lg border ${
                    inviteErrors.param3 
                      ? "border-red-500 dark:border-red-500" 
                      : "dark:border-gray-600"
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]`}
                  dir="ltr" // URLs are always LTR
                />
                {inviteErrors.param3 && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{inviteErrors.param3}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("chatInvitePreview") || "Preview:"}
            </label>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border dark:border-gray-600 min-h-[60px] min-w-0 overflow-hidden">
              {invitePreviewLoading ? (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <FaSpinner className="animate-spin" size={14} />
                  <span className="text-sm">{t("loading") || "Loading..."}</span>
                </div>
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all [overflow-wrap:anywhere]" dir="auto">
                  {inviteBodyTemplate || t("chatInvitePreviewUnavailable") || "Preview unavailable"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Buttons - Fixed */}
        <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            {t("chatInviteCancel") || "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleSendInvite}
            disabled={
              isSendingInvite || 
              !selectedTemplate || 
              !inviteParam1?.trim() || 
              !inviteParam2?.trim() || 
              (templateNeedsParam3 && !inviteParam3?.trim())
            }
            className="px-4 py-2 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSendingInvite ? (
              <>
                <FaSpinner className="animate-spin" size={14} />
                <span>{t("loading") || "Sending..."}</span>
              </>
            ) : (
              <span>{t("chatInviteSend") || "Send Invite"}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InviteTemplateModal;
